import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { canAccess } from '../utils/permissions';
import { readCache, writeCache, clearAllCache, clearCache } from '../utils/cache';
import { getFinanceTotals } from '../utils/helpers';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};

const cachedOr = (key, fallback = []) => readCache(key) || fallback;

export const AppProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading, userData } = useAuth();
  const wasAuthenticated = useRef(false);

  const [quotations, setQuotations] = useState(() => cachedOr('quotations'));
  const [invoices, setInvoices] = useState(() => cachedOr('invoices'));
  const [employees, setEmployees] = useState(() => cachedOr('employees'));
  const [paymentAccounts, setPaymentAccounts] = useState(() => cachedOr('paymentAccounts'));
  const [transactions, setTransactions] = useState(() => cachedOr('transactions'));
  const [expenses, setExpenses] = useState(() => cachedOr('expenses'));
  const [inventory, setInventory] = useState(() => cachedOr('inventory'));
  const [projectCostSheets, setProjectCostSheets] = useState(() => cachedOr('projectCostSheets'));
  const [dalale, setDalale] = useState(() => cachedOr('dalale'));
  const [appUsers, setAppUsers] = useState(() => cachedOr('appUsers'));

  const [loadingQuotations, setLoadingQuotations] = useState(() => !readCache('quotations'));
  const [loadingInvoices, setLoadingInvoices] = useState(() => !readCache('invoices'));
  const [loadingEmployees, setLoadingEmployees] = useState(() => !readCache('employees'));
  const [loadingPayments, setLoadingPayments] = useState(() => !readCache('paymentAccounts'));
  const [loadingExpenses, setLoadingExpenses] = useState(() => !readCache('expenses'));
  const [loadingInventory, setLoadingInventory] = useState(() => !readCache('inventory'));
  const [loadingProjectCostSheets, setLoadingProjectCostSheets] = useState(() => !readCache('projectCostSheets'));
  const [loadingDalale, setLoadingDalale] = useState(() => !readCache('dalale'));
  const [loadingAppUsers, setLoadingAppUsers] = useState(() => !readCache('appUsers'));

  useEffect(() => {
    clearCache('stats');
  }, []);

  const stats = useMemo(() => {
    const finance = getFinanceTotals(invoices, expenses);
    return {
      totalQuotations: quotations.length,
      pendingQuotations: quotations.filter(q => q.status === 'pending').length,
      approvedQuotations: quotations.filter(q => q.status === 'approved' || q.status === 'invoiced').length,
      totalInvoices: invoices.length,
      totalRevenue: finance.totalRevenue,
      totalExpenses: finance.totalExpenses,
      netProfit: finance.netProfit,
      totalCustomers: new Set(quotations.map(q => q.phone).filter(Boolean)).size,
      totalEmployees: employees.length
    };
  }, [quotations, invoices, expenses, employees]);

  // Payment Accounts (Public)
  useEffect(() => {
    const q = query(collection(db, 'paymentAccounts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(d => d.isActive !== false);
      setPaymentAccounts(data);
      writeCache('paymentAccounts', data);
      setLoadingPayments(false);
    }, () => setLoadingPayments(false));
    return () => unsubscribe();
  }, []);

  // Protected data
  useEffect(() => {
    if (!isAuthenticated) {
      setLoadingQuotations(false);
      setLoadingInvoices(false);
      setLoadingEmployees(false);
      setLoadingExpenses(false);
      setLoadingInventory(false);
      setLoadingProjectCostSheets(false);
      setLoadingDalale(false);
      return;
    }

    const qQuotations = query(collection(db, 'quotations'), orderBy('createdAt', 'desc'), limit(100));
    const unsubQuotations = onSnapshot(qQuotations, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setQuotations(data);
      writeCache('quotations', data);
      setLoadingQuotations(false);
    }, () => setLoadingQuotations(false));

    const qInvoices = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'), limit(100));
    const unsubInvoices = onSnapshot(qInvoices, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInvoices(data);
      writeCache('invoices', data);
      setLoadingInvoices(false);
    }, () => setLoadingInvoices(false));

    const qEmployees = query(collection(db, 'employees'), orderBy('createdAt', 'desc'));
    const unsubEmployees = onSnapshot(qEmployees, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(d => d.isActive !== false);
      setEmployees(data);
      writeCache('employees', data);
      setLoadingEmployees(false);
    }, () => setLoadingEmployees(false));

    const qTransactions = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), limit(200));
    const unsubTransactions = onSnapshot(qTransactions, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTransactions(data);
      writeCache('transactions', data);
    });

    const qExpenses = query(collection(db, 'expenses'), orderBy('createdAt', 'desc'), limit(200));
    const unsubExpenses = onSnapshot(qExpenses, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExpenses(data);
      writeCache('expenses', data);
      setLoadingExpenses(false);
    }, () => setLoadingExpenses(false));

    const qInventory = query(collection(db, 'inventory'), orderBy('createdAt', 'desc'), limit(200));
    const unsubInventory = onSnapshot(qInventory, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInventory(data);
      writeCache('inventory', data);
      setLoadingInventory(false);
    }, () => setLoadingInventory(false));

    const qProjectCosts = query(collection(db, 'projectCostSheets'), orderBy('updatedAt', 'desc'), limit(200));
    const unsubProjectCosts = onSnapshot(qProjectCosts, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProjectCostSheets(data);
      writeCache('projectCostSheets', data);
      setLoadingProjectCostSheets(false);
    }, () => setLoadingProjectCostSheets(false));

    const qDalale = query(collection(db, 'dalale'), orderBy('updatedAt', 'desc'), limit(300));
    const unsubDalale = onSnapshot(qDalale, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDalale(data);
      writeCache('dalale', data);
      setLoadingDalale(false);
    }, () => setLoadingDalale(false));

    return () => {
      unsubQuotations();
      unsubInvoices();
      unsubEmployees();
      unsubTransactions();
      unsubExpenses();
      unsubInventory();
      unsubProjectCosts();
      unsubDalale();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !canAccess(userData, 'users')) {
      setAppUsers([]);
      setLoadingAppUsers(false);
      return;
    }
    const qUsers = query(collection(db, 'users'));
    const unsub = onSnapshot(qUsers, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAppUsers(data);
      writeCache('appUsers', data);
      setLoadingAppUsers(false);
    }, () => setLoadingAppUsers(false));
    return () => unsub();
  }, [isAuthenticated, userData?.role, userData?.permissions]);

  useEffect(() => {
    if (authLoading) return;
    if (wasAuthenticated.current && !isAuthenticated) {
      clearAllCache();
      setQuotations([]);
      setInvoices([]);
      setEmployees([]);
      setTransactions([]);
      setExpenses([]);
      setInventory([]);
      setProjectCostSheets([]);
      setDalale([]);
      setAppUsers([]);
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, authLoading]);

  const getPaymentAccount = useCallback((paymentType) => {
    return paymentAccounts.find(acc => acc.paymentType === paymentType);
  }, [paymentAccounts]);

  return (
    <AppContext.Provider value={{
      quotations, invoices, employees, paymentAccounts, transactions, expenses, inventory, projectCostSheets, dalale, appUsers, stats,
      loadingQuotations, loadingInvoices, loadingEmployees, loadingPayments, loadingExpenses, loadingInventory, loadingProjectCostSheets, loadingDalale, loadingAppUsers,
      getPaymentAccount
    }}>
      {children}
    </AppContext.Provider>
  );
};
